import {
  createUserWithEmailAndPassword,
  updateProfile,
  deleteUser,
  User,
} from "firebase/auth";
import {
  initFirebase,
  clearFirebaseLocalStorage,
} from "@core/services/api/firebase";
import { apiRequest } from "@core/services/api/apiRequest";
import type { ClaimedVia } from "@core/features/invitation";

type Args = {
  data: any;
  inviteCode: string | null;
  claimedVia?: ClaimedVia | null;
  onError?: (error: Error) => void;
};

export async function registerUser({
  data,
  inviteCode,
  claimedVia,
  onError,
}: Args) {
  let firebaseUser: User | null = null;

  try {
    // Ensure firebase is initialized
    const auth = await initFirebase();

    // Create user in Firebase
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      data.email,
      data.password
    );

    // Store reference for cleanup if backend fails
    firebaseUser = userCredential.user;

    // Update display name
    await updateProfile(firebaseUser, {
      displayName: `${data.firstName} ${data.lastName}`,
    });

    // Get ID token
    const idToken = await firebaseUser.getIdToken();

    // Prepare data for API call
    const body = {
      firebase_token: idToken,
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
      ...(inviteCode ? { invite_code: inviteCode } : {}),
      ...(claimedVia ? { claimed_via: claimedVia } : {}),
      ...(data.phoneNumber ? { phone_number: data.phoneNumber } : {}),
    };

    // Send data to backend
    const response = await apiRequest("/invitation/signup/", "POST", body);

    return { authToken: idToken, ...response };
  } catch (error) {
    // Clean up Firebase user if it was created but backend failed
    if (firebaseUser) {
      try {
        await deleteUser(firebaseUser);
      } catch (deleteError) {
        console.error("Failed to clean up Firebase user:", deleteError);
      }
    }

    clearFirebaseLocalStorage();

    onError?.(error as Error);

    throw error;
  }
}
